<template>
  <!-- T00IILDDEQF2 — after a reload: one click joins the interrupted call again. -->
  <div
    v-if="interrupted && !active"
    class="coaching-call-resume"
    role="alertdialog"
    :aria-label="t('individualCoaching.call.resume.title')"
  >
    <div class="text-subtitle-2">
      {{ t('individualCoaching.call.resume.title') }}
    </div>
    <div class="text-body-2 mb-1">
      {{ t('individualCoaching.call.resume.text', {title: interrupted.occurrence.title, name: interrupted.peerName}) }}
    </div>
    <div
      v-if="interrupted.isCoach"
      class="text-caption text-medium-emphasis mb-2"
    >
      {{ t('individualCoaching.call.resume.recording') }}
    </div>
    <div class="d-flex justify-end ga-2">
      <v-btn
        variant="text"
        size="small"
        class="coaching-call-resume-dismiss"
        @click="dismiss"
      >
        {{ t('individualCoaching.call.resume.dismiss') }}
      </v-btn>
      <v-btn
        color="primary"
        variant="flat"
        size="small"
        :prepend-icon="Icons.VIDEO"
        class="coaching-call-resume-join"
        @click="resume"
      >
        {{ t('individualCoaching.call.resume.join') }}
      </v-btn>
    </div>
  </div>
  <coaching-call
    v-if="active"
    :key="`${active.clientId}|${active.occurrence.sessionId}|${active.occurrence.occurrenceDate}`"
    :client-id="active.clientId"
    :occurrence="active.occurrence"
    :peer-name="active.peerName"
    :is-coach="active.isCoach"
    :away="away"
    :auto-join="resumed"
    @back="backToRoom"
    @hungup="forget"
    @close="close"
  />
</template>

<script setup lang="ts">
//================= [CSS] =====================================

//================= [JS] ======================================
import {computed, onBeforeUnmount} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRoute, useRouter, type RouteLocationRaw} from 'vue-router';
import {Icons} from '@simpleforyou/client-core';
import {Routes} from '@/common/enums/Routes';
import {IndividualCoachingApi} from '@/common/api/individualCoaching/IndividualCoachingApi';
import {useActiveCall} from '@/composables/individualCoaching/useActiveCall';
//================= [VUE COMPONENTS] ==========================
import CoachingCall from '@/components/individualCoaching/call/CoachingCall.vue';

/**
 * T009OSKBRBOH — shows the running 1:1 call in the layout, so it outlives a
 * change of page. Away from the room the call shrinks to its bar and offers
 * the way back. Leaving the layout (logout, the other layout) ends it.
 * T00IILDDEQF2 — after a reload it offers to join the interrupted call again.
 */
const {t} = useI18n();
const {active, interrupted, resumed, close, forget, resume, dismiss} = useActiveCall();
const route = useRoute();
const router = useRouter();

const roomRoute = computed<RouteLocationRaw | null>(() => {
  if (!active.value) {
    return null;
  }
  return active.value.clientId === IndividualCoachingApi.OWN_CLIENT
    ? {name: Routes.INDIVIDUAL_COACHING_MINE}
    : {name: Routes.INDIVIDUAL_COACHING_CLIENT, params: {id: active.value.clientId}};
});

const away = computed(() => !!roomRoute.value && router.resolve(roomRoute.value).path !== route.path);

async function backToRoom(): Promise<void> {
  if (roomRoute.value) {
    await router.push(roomRoute.value);
  }
}

// Leaving the layout (logout, the other layout) ends the call and forgets it.
onBeforeUnmount(() => {
  close();
  dismiss();
});

defineExpose({away, backToRoom});
</script>

<style scoped>
.coaching-call-resume {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 1500;
  max-width: 360px;
  padding: 12px 16px;
  border-radius: 12px;
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
  box-shadow: 0 8px 28px rgba(16, 24, 40, 0.24);
}
</style>
